import { Eye, EyeOff, ExternalLink } from 'lucide-react';
import { AMENITIES } from './constants';

const StepReview = ({ 
  formData, 
  photoPreviews, 
  existingPhotos, 
  publishStatus, 
  setPublishStatus,
  isApprovedHost,
  tripId,
  onPreview,
  onPublish,
  onUnpublish,
  currentStatus,
  saving
}) => {
  const totalPhotos = (existingPhotos?.length || 0) + (photoPreviews?.length || 0);

  return (
    <div className="space-y-6">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium text-emerald-900 mb-2">Ready to publish?</h3>
        <p className="text-sm text-emerald-700">
          Review your listing details below. Choose whether to publish now or save as draft.
        </p>
      </div>

      {/* Preview Button */}
      {onPreview && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900">Preview your listing</p>
              <p className="text-xs text-blue-700 mt-1">Save as draft and see how clients will see it</p>
            </div>
            <button
              type="button"
              onClick={onPreview}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              Preview as Client
            </button>
          </div>
        </div>
      )}

      {/* View as Client Button (for edit mode) */}
      {tripId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900">Preview your listing</p>
              <p className="text-xs text-blue-700 mt-1">See exactly how clients will see your listing</p>
            </div>
            <a
              href={`/trips/${tripId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              View as Client
            </a>
          </div>
        </div>
      )}

      {/* Publish Status Selection */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Listing Status
        </label>
        {!isApprovedHost && (
          <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Your host application is pending approval. Listings will be saved as drafts and will only be visible to the public once your application is approved.
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setPublishStatus('draft')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              publishStatus === 'draft'
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 hover:border-emerald-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <EyeOff className={`w-5 h-5 ${publishStatus === 'draft' ? 'text-emerald-600' : 'text-gray-400'}`} />
              <div>
                <h4 className={`text-sm font-medium ${publishStatus === 'draft' ? 'text-emerald-900' : 'text-gray-700'}`}>
                  Save as Draft
                </h4>
                <p className="text-xs text-gray-500 mt-1">Not visible to guests</p>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => isApprovedHost && setPublishStatus('published')}
            disabled={!isApprovedHost}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              publishStatus === 'published' && isApprovedHost
                ? 'border-emerald-500 bg-emerald-50'
                : !isApprovedHost
                ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                : 'border-gray-200 hover:border-emerald-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <Eye className={`w-5 h-5 ${publishStatus === 'published' && isApprovedHost ? 'text-emerald-600' : 'text-gray-400'}`} />
              <div>
                <h4 className={`text-sm font-medium ${publishStatus === 'published' && isApprovedHost ? 'text-emerald-900' : 'text-gray-700'}`}>
                  Publish Now
                </h4>
                <p className="text-xs text-gray-500 mt-1">
                  {isApprovedHost ? 'Visible to all guests' : 'Requires approval'}
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Quick Actions for Edit Mode */}
      {currentStatus === 'published' && publishStatus === 'published' && onUnpublish && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-900">Listing is currently published</p>
              <p className="text-xs text-amber-700 mt-1">Click "Unpublish" to make it a draft, or save changes to keep it published</p>
            </div>
            <button
              type="button"
              onClick={onUnpublish}
              disabled={saving}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Unpublishing...' : 'Unpublish'}
            </button>
          </div>
        </div>
      )}

      {currentStatus === 'draft' && publishStatus === 'published' && onPublish && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-emerald-900">Listing is currently a draft</p>
              <p className="text-xs text-emerald-700 mt-1">Click "Publish Now" to make it visible to guests</p>
            </div>
            <button
              type="button"
              onClick={onPublish}
              disabled={saving}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Publishing...' : 'Publish Now'}
            </button>
          </div>
        </div>
      )}

      {/* Review Content */}
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-2">Title</h4>
          <p className="text-base text-gray-900">{formData.title}</p>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-2">Location</h4>
          <p className="text-base text-gray-900">{formData.location}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Dates</h4>
            <p className="text-base text-gray-900">
              {formData.startDate && new Date(formData.startDate).toLocaleDateString()} - {formData.endDate && new Date(formData.endDate).toLocaleDateString()}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Capacity</h4>
            <p className="text-base text-gray-900">
              {formData.capacity} guests
            </p>
          </div>
        </div>

        {formData.basePrice !== undefined && (
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Base Price</h4>
            <p className="text-base text-gray-900">
              ${formData.basePrice} per person ({formData.currency || 'USD'})
            </p>
          </div>
        )}

        {formData.packages && formData.packages.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Packages ({formData.packages.length})</h4>
            <div className="space-y-2">
              {formData.packages.map((pkg, index) => (
                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-gray-900">{pkg.name}</p>
                  <p className="text-sm text-gray-600">
                    {pkg.durationType === 'fixed' 
                      ? `${pkg.durationNights} nights` 
                      : `${pkg.durationMin}-${pkg.durationMax} nights`} • 
                    ${pkg.price} per person • 
                    {pkg.minGuests}-{pkg.maxGuests} guests
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-2">Description</h4>
          <p className="text-base text-gray-900 whitespace-pre-wrap">{formData.description}</p>
        </div>

        {totalPhotos > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Photos ({totalPhotos})</h4>
            <div className="grid grid-cols-4 gap-2">
              {[...(existingPhotos || []), ...(photoPreviews || [])].slice(0, 4).map((photo, index) => (
                <img
                  key={index}
                  src={photo}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
              ))}
            </div>
          </div>
        )}

        {formData.amenities && formData.amenities.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Amenities</h4>
            <div className="flex flex-wrap gap-2">
              {formData.amenities.map((amenityId) => {
                const amenity = AMENITIES.find((a) => a.id === amenityId);
                return (
                  <span
                    key={amenityId}
                    className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm"
                  >
                    {amenity?.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {formData.instructorName && (
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Instructor/Guide</h4>
            <p className="text-base text-gray-900">{formData.instructorName}</p>
            {formData.instructorBio && (
              <p className="text-sm text-gray-600 mt-1">{formData.instructorBio}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StepReview;

